import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import { useRecruitmentDetail } from '../../hooks/use_recruitment_detail'
import mainBgImage from '../../images/main_bg.png'
import CompanyInfoSection from '../../components/common/company_info_section'
import RelatedEnterprisesSection from '../../components/common/related_enterprises_section'
import { RelatedEnterprise } from '../../clients/kyousou_api_client'
import SendMatchingModal from '../../components/modals/send_matching_modal'
import {
  getKyousouApiClient,
  handleError,
  ApiErrorResponse,
} from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { MatchingRequestSection } from '../../components/sections/matching_request_section'
import { useEnterpriseMatchingStatus } from '../../hooks/use_enterprise_matching_status'
import { formatDate } from '../../utils/datetime_util'
import { QuillDisplay } from '../../components/form/inputs/QuillEditor'
import { SDGsCategoryLabel } from '../../enums/SDGsCategory'
import Breadcrumb from '../../components/common/breadcrumb'

const RecruitmentDetailPage: React.FC = () => {
  // ルートパラメータから募集IDを取得
  const { id } = useParams<{ id: string }>()

  // 募集詳細データと関連企業データを取得
  const { recruitment, relatedEnterprises, loading, error } =
    useRecruitmentDetail(id)

  // マッチングリクエストのステータス確認関数を取得
  const { status, confirm: matchingConfirm } = useEnterpriseMatchingStatus(
    recruitment?.enterprise?.id?.toString() ?? undefined
  )

  // ナビゲーションとAPIクライアント
  const navigate = useNavigate()
  const apiClient = useMemo(() => getKyousouApiClient(), [])
  const { showSnackBar } = useSnackBar()

  // モーダルと送信状態の管理
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 関連企業クリック時のハンドラ
  const handleEnterpriseClick = (enterprise: RelatedEnterprise) => {
    navigate(`/enterprises/detail/${enterprise.id}`)
  }

  // 応募ボタンのクリックハンドラ
  const handleApplicationClick = () => {
    setIsModalOpen(true)
  }

  // 応募送信処理
  const handleApplicationSubmit = async (message: string) => {
    if (!id) return

    setIsSubmitting(true)
    try {
      await apiClient.partnerRecruitments.requestCreate(id, { message })
      showSnackBar(['応募リクエストを送信しました'], 'success')
      setIsModalOpen(false)

      // ステータス再取得
      matchingConfirm()
    } catch (error) {
      handleError(error as ApiErrorResponse, showSnackBar)
    } finally {
      setIsSubmitting(false)
    }
  }

  // チャット画面へ遷移
  const handleChatClick = () => {
    navigate('/chat')
  }

  // ローディング状態
  if (loading && !recruitment) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="animate-pulse">
            <div className="h-8 bg-main-lightgray rounded mb-4"></div>
            <div className="h-64 bg-main-lightgray rounded mb-4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-main-lightgray rounded"></div>
                ))}
              </div>
              <div className="h-64 bg-main-lightgray rounded"></div>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  // エラーハンドリング
  if (error || !recruitment) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8 bg-main-white">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-main-darkgray mb-4">
              募集が見つかりません
            </h1>
            <p className="text-main-darkgray">
              {error || '指定された募集情報を取得できませんでした。'}
            </p>
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <Breadcrumb
        items={[
          { label: 'ホーム', path: '/dashboard' },
          { label: '共創パートナー募集一覧', path: '/recruitment' },
          { label: '募集詳細' },
        ]}
      />

      {/* メイン画像 */}
      <div className="container mx-auto">
        <img
          src={recruitment.thumbnail_url || mainBgImage}
          alt="募集メイン画像"
          className={`w-full h-80 object-cover`}
        />
      </div>

      <div className="container mx-auto px-4 py-8 bg-main-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* メインコンテンツ */}
          <div className="lg:col-span-2">
            {/* 基本情報セクション */}
            <div className="mb-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                <div className="flex-1">
                  <div className="mb-2">
                    <div className="font-bold text-2xl mb-2">
                      {recruitment.title}
                    </div>
                    <div className="text-xs text-main-darkgray flex items-center gap-1">
                      <span className="material-icons text-main-black text-sm">
                        calendar_today
                      </span>
                      公開日: {formatDate(recruitment.created_at)}
                    </div>
                  </div>
                </div>
              </div>

              {recruitment.sdgs_categories &&
                recruitment.sdgs_categories.length > 0 && (
                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">
                      {recruitment.sdgs_categories.map(
                        (categoryCode, index) => (
                          <span
                            key={index}
                            className="bg-main-lightgreen text-main-green border border-main-green px-3 py-1 rounded-full text-xs"
                          >
                            目標 {categoryCode}:{' '}
                            {SDGsCategoryLabel[categoryCode] || ''}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* 詳細説明セクション */}
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4">詳細説明</h3>
              <QuillDisplay
                content={recruitment.description}
                className="text-main-darkgray"
              />
              <hr className="border-main-darkgray mt-6" />
            </div>

            {/* 応募要件セクション */}
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-main-green rounded-full flex items-center justify-center">
                  <span className="text-main-white text-xs">✓</span>
                </span>
                応募要件
              </h3>
              {recruitment.requirements ? (
                <QuillDisplay
                  content={recruitment.requirements}
                  className="text-main-darkgray"
                />
              ) : (
                <div className="text-main-darkgray leading-relaxed">
                  応募要件の詳細はお問い合わせください。
                </div>
              )}
              <hr className="border-main-darkgray mt-6" />
            </div>

            {/* 注意事項セクション */}
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-main-red rounded-full flex items-center justify-center">
                  <span className="text-main-white text-xs">!</span>
                </span>
                注意事項
              </h3>
              {recruitment.legal_matters ? (
                <QuillDisplay
                  content={recruitment.legal_matters}
                  className="text-main-darkgray"
                />
              ) : (
                <div className="text-main-darkgray leading-relaxed">
                  注意事項についてはお問い合わせください。
                </div>
              )}
              <hr className="border-main-darkgray mt-6" />
            </div>
          </div>

          {/* サイドバー */}
          <div className="lg:col-span-1">
            <div className="p-6 sticky top-4">
              {/* アクションボタン - 自分の企業の募集でない場合のみ表示 */}
              {!recruitment.is_own_recruitment && (
                <MatchingRequestSection
                  matchingStatus={status}
                  isSubmitting={isSubmitting}
                  onClick={handleApplicationClick}
                  onChatClick={handleChatClick}
                  isCheckingStatus={loading}
                />
              )}

              {/* 企業情報 */}
              {recruitment.enterprise && (
                <CompanyInfoSection enterprise={recruitment.enterprise} />
              )}

              {/* 関連企業 */}
              {relatedEnterprises.length > 0 && (
                <RelatedEnterprisesSection
                  relatedEnterprises={relatedEnterprises}
                  onEnterpriseClick={handleEnterpriseClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 応募モーダル */}
      {recruitment && (
        <SendMatchingModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleApplicationSubmit}
          recruitmentTitle={recruitment.title}
          enterpriseName={recruitment.enterprise?.name || ''}
          isSubmitting={isSubmitting}
        />
      )}
    </AuthenticatedLayout>
  )
}

export default RecruitmentDetailPage
